// DEPENDENCIES
import React, { useState, useEffect, useRef, useCallback } from 'react';
// COMPONENTS
import MUIDataTable from 'mui-datatables';
import { Button, Modal, Form } from 'react-bootstrap';
// CUSTOM COMPONENTS
import ModalBlock from '../../../components/ModalBlock';
import SupplementaryDocumentActions from './SupplementaryDocumentActions';
import { removeSupplementaryRow, replaceSupplementaryRow } from './documentIndexRowState';
import { formatDate, getExtractionMethodLabel, getResourcePreviewType } from './systemDocumentMetadata';
import {
  createSupplementaryUploadFormData,
  isSupportedSupplementaryUpload,
  supplementaryTableColumnLabels,
  supplementaryUploadAccept,
  supplementaryUploadHelpText,
} from './supplementaryDocumentMetadata';
// SERVICES
import * as aiDocumentService from '../../../../services/aiDocumentService';

const SupplementaryDocumentsTab = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal } = props;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDocumentActions, setActiveDocumentActions] = useState({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [resourceObjectUrl, setResourceObjectUrl] = useState('');
  const [resourcePreviewType, setResourcePreviewType] = useState('other');
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState('');
  const documentRequestId = useRef(0);
  const resourceObjectUrlRef = useRef('');

  const loadTableData = useCallback(() => {
    setLoading(true);
    aiDocumentService
      .list()
      .then((res) => setData(res || []))
      .catch((ex) => showAlert({ message: ex.message, variant: 'error' }))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => {
    loadTableData();
  }, [loadTableData]);

  useEffect(
    () => () => {
      documentRequestId.current += 1;
      if (resourceObjectUrlRef.current) URL.revokeObjectURL(resourceObjectUrlRef.current);
    },
    [],
  );

  const runDocumentAction = (document, actionName, action, successMessage, onSuccess) => {
    setActiveDocumentActions((current) => ({ ...current, [document.id]: actionName }));
    return action()
      .then((result) => {
        if (onSuccess) onSuccess(result);
        showAlert({ message: successMessage, variant: 'success' });
      })
      .catch((ex) => {
        const status = ex?.response?.status;
        const message =
          status === 429
            ? 'The AI service is busy - please wait a moment and try again.'
            : ex?.response?.data?.message || ex.message || 'Something went wrong.';
        showAlert({ message, variant: 'error' });
      })
      .finally(() => {
        setActiveDocumentActions((current) => {
          const next = { ...current };
          delete next[document.id];
          return next;
        });
      });
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedFile(null);
    setUploadError('');
  };

  const uploadFile = (event) => {
    event.preventDefault();
    if (!selectedFile || isUploading) return;

    if (!isSupportedSupplementaryUpload(selectedFile.name)) {
      setUploadError(supplementaryUploadHelpText);
      return;
    }

    setIsUploading(true);
    setUploadError('');
    aiDocumentService
      .upload(createSupplementaryUploadFormData(selectedFile))
      .then((document) => {
        setData((current) => [document, ...current.filter((item) => item.id !== document.id)]);
        showAlert({ message: `"${selectedFile.name}" indexed successfully.`, variant: 'success' });
        closeUploadModal();
      })
      .catch((ex) => {
        const responseMessage = typeof ex?.response?.data === 'string' ? ex.response.data : ex?.response?.data?.message;
        setUploadError(responseMessage || ex?.message || 'The document could not be uploaded.');
      })
      .finally(() => setIsUploading(false));
  };

  const reindex = (document) =>
    runDocumentAction(
      document,
      'reindex',
      () => aiDocumentService.reindex(document.id),
      'Document reindexed successfully.',
      (updatedDocument) => setData((current) => replaceSupplementaryRow(current, updatedDocument)),
    );

  const remove = (document) => {
    runDocumentAction(
      document,
      'delete',
      () => aiDocumentService.remove(document.id),
      'Document deleted.',
      () => setData((current) => removeSupplementaryRow(current, document.id)),
    )
      .then(hideConfirmModal)
      .catch(hideConfirmModal);
  };

  const confirmDelete = (document) => {
    showConfirmModal({
      title: 'Delete Document',
      text: 'Remove this document and its indexed content?',
      rightBtnText: 'Confirm',
      btnAction: () => remove(document),
    });
  };

  const clearResourceObjectUrl = () => {
    if (resourceObjectUrlRef.current) URL.revokeObjectURL(resourceObjectUrlRef.current);
    resourceObjectUrlRef.current = '';
    setResourceObjectUrl('');
    setResourcePreviewType('other');
  };

  const viewDocument = (document) => {
    const requestId = ++documentRequestId.current;
    clearResourceObjectUrl();
    setSelectedDocument(document);
    setResourceLoading(true);
    setResourceError('');
    setIsDocumentModalOpen(true);

    aiDocumentService
      .getResource(document.id)
      .then((blob) => {
        if (requestId !== documentRequestId.current) return;
        const objectUrl = URL.createObjectURL(blob);
        resourceObjectUrlRef.current = objectUrl;
        setResourceObjectUrl(objectUrl);
        setResourcePreviewType(getResourcePreviewType(blob.type, document.fileName || document.title));
      })
      .catch((ex) => {
        if (requestId === documentRequestId.current) {
          setResourceError(ex?.response?.data?.message || ex.message || 'The document resource could not be loaded.');
        }
      })
      .finally(() => {
        if (requestId === documentRequestId.current) setResourceLoading(false);
      });
  };

  const closeDocumentModal = () => {
    documentRequestId.current += 1;
    clearResourceObjectUrl();
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
    setResourceLoading(false);
    setResourceError('');
  };

  const renderResourceContent = () => {
    if (resourceLoading) {
      return <div className='text-muted text-center py-10'>Loading document resource...</div>;
    }
    if (resourceError) {
      return (
        <div className='alert alert-danger' role='alert'>
          {resourceError}
        </div>
      );
    }
    if (!resourceObjectUrl) {
      return <div className='text-muted text-center py-10'>The original document resource is unavailable.</div>;
    }

    return (
      <div>
        <div className='d-flex justify-content-end mb-3'>
          <a className='btn btn-primary' href={resourceObjectUrl} target='_blank' rel='noopener noreferrer'>
            Open in New Tab
          </a>
        </div>
        {resourcePreviewType === 'pdf' ? (
          <iframe
            title={selectedDocument?.title || selectedDocument?.fileName || 'Document resource'}
            src={resourceObjectUrl}
            style={{ width: '100%', height: '60vh', border: 0 }}
          />
        ) : resourcePreviewType === 'image' ? (
          <img
            src={resourceObjectUrl}
            alt={selectedDocument?.title || selectedDocument?.fileName || 'Document resource'}
            style={{ display: 'block', maxWidth: '100%', maxHeight: '60vh', margin: '0 auto' }}
          />
        ) : (
          <div className='text-muted text-center py-10'>This resource cannot be previewed in the browser. Use Open in New Tab.</div>
        )}
        <div className='text-muted mt-3'>If the document does not appear, use Open in New Tab.</div>
      </div>
    );
  };

  const columns = [
    {
      name: 'id',
      label: supplementaryTableColumnLabels.actions,
      options: {
        filter: false,
        sort: false,
        download: false,
        setCellProps: () => ({ style: { width: '28%' }, 'data-aes-field': 'actions' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'actions' }),
        customBodyRenderLite: (dataIndex) => {
          const document = data[dataIndex];
          return (
            <SupplementaryDocumentActions
              document={document}
              activeAction={document ? activeDocumentActions[document.id] : null}
              onView={viewDocument}
              onReindex={reindex}
              onDelete={confirmDelete}
            />
          );
        },
      },
    },
    {
      name: 'title',
      label: supplementaryTableColumnLabels.document,
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'document', 'data-aes-label': 'Document' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'document' }),
        customBodyRenderLite: (dataIndex) => (
          <div className='font-weight-bolder text-dark text-break'>
            {data[dataIndex]?.fileName || data[dataIndex]?.title || <>&mdash;</>}
          </div>
        ),
      },
    },
    {
      name: 'extractionMethod',
      label: supplementaryTableColumnLabels.extraction,
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'extraction', 'data-aes-label': 'Extraction' }),
        customBodyRenderLite: (dataIndex) => getExtractionMethodLabel(data[dataIndex]?.extractionMethod) || <>&mdash;</>,
      },
    },
    {
      name: 'indexedAt',
      label: supplementaryTableColumnLabels.indexedAt,
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'updated', 'data-aes-label': 'Indexed At' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'updated' }),
        customBodyRenderLite: (dataIndex) => formatDate(data[dataIndex]?.indexedAt) || <>&mdash;</>,
      },
    },
  ];

  return (
    <>
      <Modal centered show={isUploadModalOpen} onHide={() => !isUploading && closeUploadModal()}>
        <form onSubmit={uploadFile}>
          <Modal.Header closeButton>
            <Modal.Title>Upload Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {uploadError && (
              <div className='alert alert-danger' role='alert'>
                {uploadError}
              </div>
            )}
            <Form.Group>
              <Form.Label>Document</Form.Label>
              <Form.Control
                type='file'
                accept={supplementaryUploadAccept}
                disabled={isUploading}
                onChange={(event) => {
                  const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
                  setSelectedFile(file);
                  setUploadError(file && !isSupportedSupplementaryUpload(file.name) ? supplementaryUploadHelpText : '');
                }}
              />
              {selectedFile && <Form.Text className='text-muted'>Selected file: {selectedFile.name}</Form.Text>}
              <Form.Text className='text-muted'>{supplementaryUploadHelpText} Extraction is selected automatically.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' type='button' onClick={closeUploadModal} disabled={isUploading}>
              Close
            </Button>
            <Button variant='primary' type='submit' disabled={!selectedFile || isUploading}>
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <ModalBlock
        centered
        size='xl'
        show={isDocumentModalOpen}
        onHide={closeDocumentModal}
        closeAction
        closeActionOnClick={closeDocumentModal}
        contentHeader={selectedDocument?.title || selectedDocument?.fileName || 'Document'}
        subtitle={selectedDocument?.fileName || selectedDocument?.sourceUrl || ''}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto', marginTop: '20px' }}>
          {renderResourceContent()}
        </div>
      </ModalBlock>

      <div className='px-6 py-5 aes-supplementary-documents'>
        <div className='d-flex flex-wrap align-items-start justify-content-between'>
          <div className='mb-16 px-3'>
            <h4 className='font-size-h3 font-weight-bolder text-dark mb-1'>Supplementary Documents</h4>
            <div className='text-muted fz-xs'>Documents added specifically to provide supplemental knowledge to the AES Assistant.</div>
          </div>
          <div className='card-toolbar mb-4'>
            <button
              type='button'
              onClick={() => setIsUploadModalOpen(true)}
              disabled={isUploading}
              className='btn btn-success font-weight-bolder font-size-sm'
            >
              Upload Document
            </button>
          </div>
        </div>
        <div className='container-fluid aes-admin-listing p-0 mt-2'>
          <MUIDataTable
            className='aes-admin-listing-surface'
            data={data}
            columns={columns}
            options={{
              responsive: 'standard',
              rowsPerPage: 25,
              rowsPerPageOptions: [25, 50, 100],
              selectableRows: 'none',
              download: false,
              print: false,
              viewColumns: false,
              textLabels: { body: { noMatch: loading ? 'Loading supplementary documents...' : 'No supplementary documents are available.' } },
              setTableProps: () => ({ className: 'aes-admin-listing-table aes-supplementary-documents-table' }),
            }}
          />
        </div>
      </div>
    </>
  );
};

export default SupplementaryDocumentsTab;
